import SearchIcon from "@redotech/redo-icons/search-sm_filled.svg";
import { memo, type ReactNode } from "react";
import { RedoTextInput } from "./arbiter-components/input/redo-text-input";
import { Flex } from "./flex";
import { JsonValueEditor, jsonValueMatchesSearch } from "./json-value-editor";
import { Text } from "./text";

const ERROR_STAGE_LABELS: Record<string, string> = {
  typeCheck: "Type check failed",
  inputValidation: "Input is invalid for this contract",
  execution: "Code threw while running",
  outputValidation: "Return value doesn't match the contract's Output",
};

export type ExtensionArtifactTestRunState =
  | { status: "idle" }
  | { status: "running" }
  | {
      status: "done";
      result: {
        ok: boolean;
        logs: string;
        output?: unknown;
        error?: { stage: string; message: string };
      };
    }
  | { status: "failed"; message: string };

export const ContentSearchControl = memo(function ContentSearchControl({
  placeholder,
  searchString,
  setSearchString,
}: {
  placeholder: string;
  searchString: string;
  setSearchString: (searchString: string) => void;
}) {
  return (
    <RedoTextInput
      fullWidth
      IconLeading={SearchIcon}
      placeholder={placeholder}
      setValue={setSearchString}
      size="sm"
      value={searchString}
    />
  );
});

export const SearchableSectionHeader = memo(function SearchableSectionHeader({
  children,
  leadingIcon,
  onClick,
  title,
}: {
  children: ReactNode;
  leadingIcon: ReactNode;
  onClick: () => void;
  title: string;
}) {
  return (
    <Flex align="center" gap="sm" justify="space-between" w="full">
      <Flex
        align="center"
        as="button"
        bgColor="none"
        borderStyle="none"
        borderWidth="none"
        cursor="pointer"
        gap="xs"
        onClick={onClick}
        p="none"
        type="button"
      >
        {leadingIcon}
        <Text fontSize="xs" fontWeight="medium">
          {title}
        </Text>
      </Flex>
      <Flex minW="none" w="half">
        {children}
      </Flex>
    </Flex>
  );
});

export const TestRunOutput = memo(function TestRunOutput({
  run,
  searchString,
}: {
  run: ExtensionArtifactTestRunState;
  searchString: string;
}) {
  switch (run.status) {
    case "idle":
      return (
        <Text fontSize="xs" textColor="secondary">
          Run your code to see its output here.
        </Text>
      );
    case "running":
      return (
        <Text fontSize="xs" textColor="secondary">
          Running…
        </Text>
      );
    case "failed":
      return (
        <Text fontSize="xs" textColor="error">
          Test run failed: {run.message}
        </Text>
      );
    case "done":
      return (
        <>
          {run.result.error ? (
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" fontWeight="medium" textColor="error">
                {ERROR_STAGE_LABELS[run.result.error.stage] ?? "Run failed"}
              </Text>
              <Text
                fontFamily="monospace"
                fontSize="xs"
                textColor="error"
                whiteSpace="pre-wrap"
              >
                {run.result.error.message}
              </Text>
            </Flex>
          ) : (
            <JsonSearchResults
              searchString={searchString}
              value={run.result.output}
            />
          )}
          {run.result.logs ? (
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" fontWeight="medium">
                Console
              </Text>
              <Text
                fontFamily="monospace"
                fontSize="xs"
                textColor="secondary"
                whiteSpace="pre-wrap"
              >
                {run.result.logs}
              </Text>
            </Flex>
          ) : null}
        </>
      );
  }
});

const JsonSearchResults = memo(function JsonSearchResults({
  searchString,
  value,
}: {
  searchString: string;
  value: unknown;
}) {
  if (searchString.trim() && !jsonValueMatchesSearch(value, searchString)) {
    return (
      <Text fontSize="xs" textColor="secondary">
        No matching fields
      </Text>
    );
  }
  if (typeof value === "object" && value !== null) {
    return (
      <Flex dir="column" gap="xs" w="full">
        <JsonValueEditor
          defaultCollapsed={false}
          label="output"
          readonly
          searchLabel={false}
          searchString={searchString}
          setValue={() => {}}
          value={value}
        />
      </Flex>
    );
  }
  return (
    <Text fontFamily="monospace" fontSize="xs" textColor="secondary">
      {String(value)}
    </Text>
  );
});
